import {
  Card,
  Checkbox,
  CustomDatePicker,
  Fieldset,
  Form,
  Grid,
  Select,
  Spacer,
} from '@components/common'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import ReportFilters from '../ReportFilters'
import { defaultReportFilterFields } from '../ReportFilters/report-filter-fields'
import { PurchaseAnalysisSpreadsheetReportParams, INTERVALS } from '@api/models'
import { ActionButtons } from '../General'
import { useReport } from '@hooks'
import { useEffect } from 'react'
import { getTodaysDate } from '@utils/basic'
import { ReactSelectOptions } from '@components/common/Select/Select.types'
import { ReportsApi } from '@api'
import { reportErrorHandler, reportResponseHandler } from '@utils/reports'
import { useMemorizeReport } from '../General/MemorizeReports/useMemorizeReport'
import { PURCHASE_ANALYSIS_SPREADSHEET_REPORT_PATH } from '@constants'
import MemorizeReports from '../General/MemorizeReports/MemorizeReport'
import { useFormMethods } from '../General/useFormMethods'
import { useLocation } from 'react-router-dom'

const defaultValues: PurchaseAnalysisSpreadsheetReportParams = {
  assetCompanyID: 0,
  filter: [],
  fromDate: getTodaysDate(),
  toDate: getTodaysDate(),
  includeCaps: false,
  interval: INTERVALS[0],
  includeDisposals: false,
  fileFormat: 'pdf',
}

async function onSubmitHandler(values: PurchaseAnalysisSpreadsheetReportParams) {
  await ReportsApi.getInstance()
    .getPurchaseAnalysisSpreadsheet(values)
    .then(data => reportResponseHandler(data, values.fileFormat))
    .catch(reportErrorHandler)
}

const PurchaseAnalysisSpreadsheetReportForm = () => {
  const location = useLocation()
  const locationState: any = location.state

  const {
    methods,
    isRetrievingTemplate,
    isEditTemplate,
    selectedTemplateDetails,
  } = useFormMethods(locationState, defaultValues)

  const {
    formState: { isSubmitting, errors },
    control,
    setValue,
    watch,
  } = methods

  const fileFormat = watch('fileFormat')
  const { isLoading, isSuccess, isError, isNoCompanies, assetCompanyID } = useReport()

  const {
    handleMemorizeTemplateSubmit,
    isMemorizeTemplateModalOpen,
    closeMemorizeTemplateModal,
    triggerMemorizeTemplateModal,
    handleEditTemplateSubmit,
  } = useMemorizeReport({
    methods,
    currentReportPath: PURCHASE_ANALYSIS_SPREADSHEET_REPORT_PATH,
  })

  useEffect(() => {
    if (isSuccess) {
      setValue('assetCompanyID', assetCompanyID)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, assetCompanyID])

  const intervalSelectOptions: ReactSelectOptions = INTERVALS.map(interval => ({
    label: interval,
    value: interval,
  }))

  function getCheckBoxProps(field: any) {
    return {
      checked: field.value,
      onChange: field.onChange,
      ref: field.ref,
      disabled: isSubmitting,
    }
  }

  function getSelectProps(field, options: ReactSelectOptions) {
    return {
      options,
      onChange: val => field.onChange(val?.value),
      value: options.find(c => field.value === c.value),
      ref: field.ref,
      disabled: isSubmitting,
    }
  }

  function getDateProps(field: any) {
    return {
      onChange: field.onChange,
      ref: field.ref,
      disabled: isSubmitting,
      select: field.value,
      onBlur: field.onBlur,
      error: errors[field] !== undefined,
      errorMsg: errors[field]?.message,
    }
  }

  if (isLoading) {
    return <div>Loading...</div>
  }

  if (isError) {
    return <div>Error</div>
  }

  if (isNoCompanies) {
    return <div>No companies found</div>
  }

  return (
    <>
      <FormProvider {...methods}>
        <Form onSubmit={methods.handleSubmit(onSubmitHandler)}>
          <Card title={'Report Parameters'}>
            <Grid>
              <Grid.Item>
                <Fieldset legend="Time Selections">
                  <Grid>
                    <Grid.Item s={4}>
                      <Controller
                        control={control}
                        name="fromDate"
                        render={({ field }) => (
                          <CustomDatePicker {...getDateProps(field)} label="From" />
                        )}
                      />
                    </Grid.Item>
                    <Grid.Item s={4}>
                      <Controller
                        control={control}
                        name="toDate"
                        render={({ field }) => (
                          <CustomDatePicker {...getDateProps(field)} label="To" />
                        )}
                      />
                    </Grid.Item>
                    <Grid.Item s={4}>
                      <Controller
                        control={control}
                        name="interval"
                        render={({ field }) => (
                          <Select
                            {...getSelectProps(field, intervalSelectOptions)}
                            label="Interval"
                          />
                        )}
                      />
                    </Grid.Item>
                  </Grid>
                </Fieldset>
              </Grid.Item>

              <Grid.Item>
                <Fieldset legend={'Options'}>
                  <Grid rowGap="mini">
                    <Grid.Item>
                      <Controller
                        control={control}
                        name="includeCaps"
                        render={({ field }) => (
                          <Checkbox
                            label="Include Capitalizations"
                            {...getCheckBoxProps(field)}
                          />
                        )}
                      />
                    </Grid.Item>
                    <Grid.Item>
                      <Controller
                        control={control}
                        name="includeDisposals"
                        render={({ field }) => (
                          <Checkbox
                            label="Include Disposals"
                            {...getCheckBoxProps(field)}
                          />
                        )}
                      />
                    </Grid.Item>
                  </Grid>
                </Fieldset>
              </Grid.Item>

              <Grid.Item>
                <ReportFilters
                  filtersTemplateData={
                    isRetrievingTemplate
                      ? locationState.templateDetails.templateData.filter
                      : []
                  }
                  filterFields={defaultReportFilterFields}
                  includesTotals={fileFormat === 'pdf'}
                />
              </Grid.Item>
            </Grid>
          </Card>
          <Spacer y="default" />
          <ActionButtons
            isSubmitting={isSubmitting}
            triggerMemorizeTemplateModal={triggerMemorizeTemplateModal}
            isEditTemplate={isEditTemplate}
          />
        </Form>
      </FormProvider>

      <MemorizeReports
        isMemorizeTemplateModalOpen={isMemorizeTemplateModalOpen}
        closeMemorizeTemplateModal={closeMemorizeTemplateModal}
        handleMemorizeTemplateSubmit={handleMemorizeTemplateSubmit}
        handleEditTemplateSubmit={handleEditTemplateSubmit}
        isEditTemplate={isEditTemplate}
        selectedTemplateDetails={selectedTemplateDetails}
      />
    </>
  )
}

export default PurchaseAnalysisSpreadsheetReportForm
